import React, { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Clock, ShoppingCart, ArrowRight, Trash2, Gift, Package, AlertTriangle } from 'lucide-react';
import { useApp } from '../App';
import { removeFromCart } from '../lib/api';
import CheckoutModal from './CheckoutModal';
import { toast } from 'sonner';

const CartItem = ({ chest, onRemove, removing }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isFree = chest.is_free || chest.price === 0;
  const isRemoving = removing === chest.chest_number;

  useEffect(() => {
    const update = () => {
      if (!chest.reserved_until) { setTimeLeft('Apartado'); return; }
      const now = new Date();
      const until = new Date(chest.reserved_until);
      const diff = until - now;
      if (diff <= 0) {
        setTimeLeft('--:--');
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${mins}:${String(secs).padStart(2, '0')}`);
      setUrgent(mins < 3);
    };
    update();
    const i = setInterval(update, 1000);
    return () => clearInterval(i);
  }, [chest.reserved_until]);

  return (
    <div className="flex items-center justify-between p-3 bg-[#121521] rounded-xl border border-white/[0.08]" data-testid={`cart-item-${chest.chest_number}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          isFree ? 'bg-[rgba(53,208,127,0.1)] border border-[rgba(53,208,127,0.25)]'
            : 'bg-[rgba(255,198,64,0.1)] border border-[rgba(255,198,64,0.25)]'
        }`}>
          {isFree ? (
            <Gift size={16} className="text-[#35D07F]" />
          ) : (
            <span className="text-xs font-heading font-bold text-[#FFC640]">#{String(chest.chest_number).padStart(4, '0')}</span>
          )}
        </div>
        <div>
          {isFree ? (
            <>
              <span className="text-sm font-heading font-bold text-[#35D07F]">GRATIS</span>
              <span className="text-xs text-[#F7F2E7]/50 ml-2">#{String(chest.chest_number).padStart(4, '0')}</span>
            </>
          ) : (
            <span className="text-lg font-heading font-bold text-[#FFC640] tabular-nums">${chest.price?.toFixed(2)}</span>
          )}
          <div className={`flex items-center gap-1 text-xs ${urgent ? 'text-amber-400' : 'text-[#4BA3FF]'}`}>
            <Clock size={12} />
            <span className="font-medium">Apartado · {timeLeft}</span>
          </div>
        </div>
      </div>
      {confirmDelete ? (
        <div className="flex items-center gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(chest.chest_number); setConfirmDelete(false); }}
            disabled={isRemoving}
            className="px-2.5 py-1 rounded-lg bg-red-500/20 text-red-400 text-xs font-semibold hover:bg-red-500/30 transition-colors"
          >
            {isRemoving ? '...' : 'Sí'}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }}
            className="px-2.5 py-1 rounded-lg bg-white/[0.06] text-[#F7F2E7]/50 text-xs hover:bg-white/[0.1] transition-colors"
          >
            No
          </button>
        </div>
      ) : (
        <button
          onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}
          className="p-2 rounded-lg hover:bg-red-500/10 text-[#F7F2E7]/40 hover:text-red-400 transition-colors"
          aria-label="Eliminar cofre"
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );
};

const CartDrawer = ({ open, onClose }) => {
  const { cart, fetchCart, selectedRaffleId } = useApp();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [removing, setRemoving] = useState(null);

  // Auto-refresh cart when drawer opens
  useEffect(() => {
    if (open && selectedRaffleId) fetchCart();
  }, [open, selectedRaffleId, fetchCart]);

  // Auto-refresh every 15s while open to clear expired items
  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => fetchCart(), 15000);
    return () => clearInterval(interval);
  }, [open, fetchCart]);

  const handleRemove = async (chestNumber) => {
    setRemoving(chestNumber);
    try {
      const res = await removeFromCart(selectedRaffleId, chestNumber);
      await fetchCart();
      if (res.data.blocked) {
        toast.error('Tu cuenta ha sido bloqueada por 2 horas por seguridad anti-mapeo.');
      } else if (res.data.remaining_before_block <= 1) {
        toast.warning(`Cofre eliminado. ¡ATENCIÓN! Te queda ${res.data.remaining_before_block} descarte antes del bloqueo.`);
      } else {
        toast.success(`Cofre #${String(chestNumber).padStart(4, '0')} eliminado. Descartes: ${res.data.discard_count}/3`);
      }
    } catch (err) {
      await fetchCart();
      toast.error(err.response?.data?.detail || 'Error al eliminar cofre');
    }
    setRemoving(null);
  };

  const freeCount = cart.chests?.filter(c => c.is_free || c.price === 0).length || 0;
  const discardInfo = cart.discard_count !== undefined ? cart.discard_count : null;

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="bg-[#0E1016] border-l border-white/[0.08] w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-drawer">
          <SheetHeader className="p-6 pb-4 border-b border-white/[0.08]">
            <SheetTitle className="text-[#F7F2E7] font-heading flex items-center gap-2">
              <ShoppingCart size={20} className="text-[#FFC640]" />
              Mi Carrito
              {cart.count > 0 && (
                <span className="text-sm bg-[#FFC640]/10 text-[#FFC640] px-2 py-0.5 rounded-full">
                  {cart.count} cofre{cart.count !== 1 ? 's' : ''}
                </span>
              )}
            </SheetTitle>
          </SheetHeader>

          {cart.count === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#121521] border border-white/[0.08] flex items-center justify-center mb-4">
                <ShoppingCart size={28} className="text-[#F7F2E7]/30" />
              </div>
              <p className="text-[#F7F2E7]/60 text-sm">Tu carrito está vacío</p>
              <p className="text-[#F7F2E7]/40 text-xs mt-1">Abre cofres para apartarlos automáticamente</p>
            </div>
          ) : (
            <>
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-3" data-testid="cart-item-list">
                  {cart.chests.map((chest) => (
                    <CartItem key={chest.chest_number} chest={chest} onRemove={handleRemove} removing={removing} />
                  ))}
                </div>
              </ScrollArea>

              <div className="p-5 pt-4 border-t border-white/[0.08] space-y-3">
                {/* Anti-mapping discard counter */}
                <div className="flex items-center gap-2 p-2 rounded-lg bg-[rgba(255,180,50,0.05)] border border-[rgba(255,180,50,0.12)]">
                  <AlertTriangle size={14} className="text-amber-400/70 flex-shrink-0" />
                  <span className="text-[10px] text-amber-400/70">
                    Eliminar cofres sin pagar cuenta como descarte (3 descartes = bloqueo 2h)
                  </span>
                </div>

                {/* Mode indicator - NOT shown for FREE raffles */}
                {cart.is_free_raffle ? (
                  <div className="flex items-center gap-2 p-2.5 rounded-xl bg-[rgba(53,208,127,0.08)] border border-[rgba(53,208,127,0.2)]">
                    <Gift size={14} className="text-[#35D07F] flex-shrink-0" />
                    <span className="text-xs text-[#35D07F] font-medium">Sorteo Gratis — Premio principal: $8,000</span>
                  </div>
                ) : cart.count >= 3 ? (
                  <div className="flex items-center gap-2 p-2.5 rounded-xl bg-[rgba(75,163,255,0.08)] border border-[rgba(75,163,255,0.2)]">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#4BA3FF"/></svg>
                    <span className="text-xs text-[#4BA3FF] font-medium">¡MODO DIAMANTE activado! Premio: $30,000</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-2.5 rounded-xl bg-[rgba(255,198,64,0.06)] border border-[rgba(255,198,64,0.15)]">
                    <Package size={14} className="text-[#FFC640] flex-shrink-0" />
                    <span className="text-xs text-[#FFC640]/80">MODO NORMAL – agrega {3 - cart.count} cofre{3 - cart.count !== 1 ? 's' : ''} más para DIAMANTE</span>
                  </div>
                )}

                {freeCount > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[#35D07F]"><Gift size={12} className="inline mr-1" />{freeCount} cofre{freeCount !== 1 ? 's' : ''} gratis</span>
                    <span className="text-[#35D07F] font-heading">$0.00</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#F7F2E7]/70">Total a pagar:</span>
                  <span className="text-2xl font-heading font-bold text-[#FFC640] tabular-nums" data-testid="cart-total-amount">
                    ${cart.total?.toFixed(2)}
                  </span>
                </div>
                <Button
                  onClick={() => { onClose(); setCheckoutOpen(true); }}
                  className="w-full h-12 bg-[#FFC640] text-[#090A0D] font-heading font-semibold text-base hover:bg-[#FFD36B] rounded-xl shadow-[0_10px_28px_rgba(0,0,0,0.45)]"
                  data-testid="cart-continue-button"
                >
                  Continuar
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
    </>
  );
};

export default CartDrawer;
